import Button from '@/Components/Button/Button';
import classNames from 'classnames';
import React, { PropsWithChildren, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import Styles from './Dialog.module.scss';

function DialogTitle({ children, onClose }: Readonly<{ onClose?: () => void; children: string | React.ReactElement | React.ReactElement[] }>): React.ReactElement {
    if (onClose) {
        return (
            <div className={Styles.title}>
                <div>{children}</div>
                <Button onClick={() => onClose()} silent onDark small>
                    X
                </Button>
            </div>
        );
    }

    return (
        <div className={Styles.title}>
            {children}
        </div>
    );
}

function DialogContent({ center, compact, flexCenter, children }: Readonly<{ center?: boolean; compact?: boolean; flexCenter?: boolean; children: React.ReactElement | React.ReactElement[] }>): React.ReactElement {
    const classes = classNames(Styles.content, {
        [Styles.center]: center,
        [Styles.smallPadding]: compact,
        [Styles.flexCenter]: flexCenter,
    });

    return (
        <div className={classes}>
            {children}
        </div>
    );
}

function DialogButtons({ children, onClose }: Readonly<{ children?: React.ReactElement | React.ReactElement[]; onClose?: () => void }>): React.ReactElement {
    const { t } = useTranslation();

    if (onClose) {
        return (
            <div className={Styles.buttons}>
                <div>
                    {children}
                    <Button onClick={() => onClose()}>
                        {t('Close')}
                    </Button>
                </div>

            </div>
        );
    }
    return (
        <div className={Styles.buttons}>
            {children}
        </div>
    );
}

export interface DialogProps {
    className?: string;
    closeOnOutsideClick?: boolean;
    draggable?: boolean;
    open: boolean;
    onClose?: () => void;
    width?: 'default' | 'auto' | 'small';
}

function Dialog(props: Readonly<PropsWithChildren<DialogProps>>): React.ReactElement {
    const { children, className, closeOnOutsideClick, draggable, onClose, open, width = 'default' } = props;
    const diagRef = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        if (!diagRef.current) {
            return;
        }
        if (open) {
            diagRef.current.showModal();
        }
        else {
            diagRef.current.close();
        }
    }, [open]);

    const dialogClasses = classNames(Styles.dialog, className);

    const handleOutsideClick = (event: React.MouseEvent<HTMLDialogElement>): void => {
        if (!closeOnOutsideClick || !onClose) {
            return;
        }
        if (event.target === diagRef.current) {
            onClose();
        }
    };

    const hasButtons = React.Children
        .toArray(children)
        .some((child) => React.isValidElement(child) && child.type === DialogButtons);
    if (hasButtons) {
        return (
            <Draggable disabled={!draggable} nodeRef={diagRef}>
                <dialog ref={diagRef} className={dialogClasses} onClick={handleOutsideClick}>
                    {children}
                </dialog>
            </Draggable>
        );
    }
    return (
        <Draggable disabled={!draggable} nodeRef={diagRef}>
            <dialog ref={diagRef} className={dialogClasses} onClick={handleOutsideClick}>
                {children}
                <DialogButtons onClose={onClose} />
            </dialog>
        </Draggable>
    );
}

export {
    Dialog, DialogButtons, DialogContent, DialogTitle,
};
